/* 
  MQTT device status component.
  Connects to the selected MIRA device via AWS IoT Core MQTT over WebSocket.

  Auth strategy:
    - If Identity Pool is configured (REACT_APP_IDENTITY_POOL_ID), uses
      temporary STS credentials from Cognito → no static keys in the bundle.
    - Otherwise, falls back to legacy static keys from env vars.

  Uses MQTT from aws-iot-device-sdk-v2 (browser build = WebSocket only).

  Input: selectedDevice — device object from DynamoDB (MIRA_TB table).
*/

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { mqtt, iot } from 'aws-iot-device-sdk-v2';
import { getCredentials } from '../utils/awsClients';
import { Paper, Typography, Grid, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Box, Button, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const IOT_ENDPOINT = process.env.REACT_APP_ENDPOINT;
const REGION = 'us-east-1';
// 5 minutes with no message → mark device offline
const OFFLINE_TIMEOUT_MS = 300000;

const DeviceStatus = ({ selectedDevice }) => {
  const [message, setMessage] = useState('');
  const [temperature, setTemperature] = useState(null);
  const [experimentRunning, setExperimentRunning] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Refs to manage connection lifecycle and prevent race conditions
  const connectionRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(false);

  /**
   * Build an MQTT connection config using the best available credentials.
   * Identity Pool temp creds when available, static keys as fallback.
   */
  const buildMqttConfig = useCallback(async (deviceId) => {
    const creds = await getCredentials();

    const config = iot.AwsIotMqttConnectionConfigBuilder.new_builder_for_websocket()
      .with_clean_session(true)
      .with_client_id(`miravision-web-${deviceId}-${Date.now()}`)
      .with_endpoint(IOT_ENDPOINT)
      .with_credentials(
        REGION,
        creds.accessKeyId,
        creds.secretAccessKey,
        creds.sessionToken || undefined
      )
      .with_keep_alive_seconds(30)
      .build();

    return config;
  }, []);

  /**
   * Safely disconnect an existing MQTT connection.
   */
  const disconnectSafely = useCallback(async (conn, deviceId) => {
    if (!conn) return;
    try {
      await conn.unsubscribe(`${deviceId}/response/status`);
    } catch {
      // Already unsubscribed or connection lost — safe to ignore
    }
    try {
      await conn.disconnect();
    } catch {
      // Already disconnected — safe to ignore
    }
  }, []);

  useEffect(() => {
    // Reset UI state when device changes
    setMessage('');
    setTemperature(null);
    setExperimentRunning(null);
    setSnackbarOpen(false);
    setIsExpanded(false);
    abortRef.current = false;

    if (!selectedDevice?.MIRA_device_id) return;

    const deviceId = selectedDevice.MIRA_device_id;

    const initializeConnection = async () => {
      // Disconnect any previous connection
      const prevConn = connectionRef.current;
      connectionRef.current = null;
      if (prevConn) {
        await disconnectSafely(prevConn, deviceId);
      }

      // Bail out if the effect was cleaned up (device changed again)
      if (abortRef.current) return;

      try {
        const config = await buildMqttConfig(deviceId);
        if (abortRef.current) return;

        const client = new mqtt.MqttClient();
        const newConnection = client.new_connection(config);
        connectionRef.current = newConnection;

        // Swallow async errors fired during/after disconnect — aws-crt throws
        // "Connection closed" errors even on intentional teardown.
        newConnection.on('error', (err) => {
          if (abortRef.current || !connectionRef.current) {
            // Expected during cleanup — suppress
            return;
          }
          const msg = err?.message || String(err);
          if (msg.includes('Connection closed')) {
            // Expected transient error — don't spam the console
            return;
          }
          console.error('MQTT connection error:', err);
        });

        newConnection.on('disconnect', () => {
          // no-op: connection state is reflected by experimentRunning/Offline label
        });

        await newConnection.connect();
        if (abortRef.current) {
          await disconnectSafely(newConnection, deviceId);
          return;
        }

        // Subscribe to device status topic
        newConnection.subscribe(
          `${deviceId}/response/status`,
          mqtt.QoS.AtLeastOnce,
          (_topic, payload) => {
            if (abortRef.current) return;

            const decoder = new TextDecoder('utf8');
            const decodedMessage = decoder.decode(new Uint8Array(payload));
            setMessage(decodedMessage);

            try {
              const msgJSON = JSON.parse(decodedMessage);
              const temp = (parseFloat(msgJSON['temperature-1']) + parseFloat(msgJSON['temperature-2'])) / 2;
              const isExperimentRunning = msgJSON['experiment_running'] === 'True';
              setExperimentRunning(isExperimentRunning);
              setTemperature(temp);
            } catch {
              // Malformed message — keep the raw text but don't update gauges
            }

            // Reset offline timer
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
              setSnackbarOpen(true);
              setMessage('');
              setTemperature(null);
              setExperimentRunning(null);
              setIsExpanded(false);
            }, OFFLINE_TIMEOUT_MS);
          }
        );
      } catch (error) {
        console.error('Error connecting to AWS IoT:', error);
      }
    };

    initializeConnection();

    // Cleanup on unmount or device change
    return () => {
      abortRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const conn = connectionRef.current;
      connectionRef.current = null; // Null out BEFORE disconnect so error handler suppresses
      if (conn) {
        // Fire-and-forget — errors are suppressed by the handler since abortRef=true
        disconnectSafely(conn, deviceId).catch(() => {});
      }
    };
  }, [selectedDevice, buildMqttConfig, disconnectSafely]);  

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleExperimentControl = () => {
    if (experimentRunning) {
      setDialogOpen(true);
    } else {
      const conn = connectionRef.current;
      if (!conn) return;
      conn.publish(
        `${selectedDevice.MIRA_device_id}/experiment/control`,
        JSON.stringify({ command: 'start' }),
        mqtt.QoS.AtLeastOnce
      );
    }
  };

  const confirmStopExperiment = () => {
    const conn = connectionRef.current;
    if (!conn) return;
    conn.publish(
      `${selectedDevice.MIRA_device_id}/experiment/control`,    
      JSON.stringify({ 
        device_id : selectedDevice.MIRA_device_id,
        command: 'stop' 
      }),
      mqtt.QoS.AtLeastOnce
    );
    setDialogOpen(false); 
  };

  //Parse MQTT status msg and display it
  const renderExperimentDetails = (msgJSON) => {
    if (experimentRunning) {
      return (
        <>
          <Typography variant="body1">Task: {msgJSON.task}</Typography>
          <Typography variant="body1">Elapsed: {msgJSON.elapsed}</Typography>
          <Typography variant="body1">Start Time: {msgJSON.start_time}</Typography>
          <Typography variant="body1">Duration: {msgJSON.duration} hours</Typography>
          <Typography variant="body1">Setpoint: {msgJSON.setpoint}°C</Typography>
        </>
      );
    } else {
      return (
        <>
          <Typography variant="body1">Incubator Name: {msgJSON.incubator_name}</Typography>
          <Typography variant="body1">Last Updated: {msgJSON.timestamp}</Typography>
        </>
      );
    }
  };

  //Opens the status only if there are incoming MQTT msgs
  const handleAccordionOpen = () => {
    if (experimentRunning === null) {
      setSnackbarOpen(true);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div id={selectedDevice.MIRA_device_id}>
      <Accordion
        id={selectedDevice.MIRA_device_id}
        sx={{
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
        expanded={isExpanded}
        onChange={handleAccordionOpen} 
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}
          aria-controls="panel1a-content"
          id={selectedDevice.MIRA_device_id}
          sx={{ backgroundColor: '#1e1e1e', color: '#ffffff' }}
        >
          <Typography variant="h5" sx={{ color: '#ffffff' }}>
            {selectedDevice.device_name}
            {experimentRunning === true ? (
              <Typography color="green" display="inline" sx={{ paddingLeft: 2 }}>
                Running
              </Typography>
            ) : experimentRunning === false ? (
              <Typography color="red" display="inline" sx={{ paddingLeft: 2 }}>
                Idle
              </Typography>
            ) : (
              <Typography color="orange" display="inline" sx={{ paddingLeft: 2 }}>
                Offline
              </Typography>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails 
          id={selectedDevice.MIRA_device_id} sx={{ backgroundColor: '#1e1e1e', color: '#ffffff' }}>
          <Paper elevation={20} sx={{ padding: '20px', margin: 'auto', backgroundColor: '#1e1e1e', color: '#ffffff' }}>
            <Grid container rowSpacing={2} justifyContent="center" spacing={2}>
              <Grid item xs={12} sm={6} align="center">
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  Temperature
                </Typography>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={(temperature / 50) * 100} 
                    size={100}
                    thickness={4}
                    sx={{ color: temperature >= 30 ? 'red' : 'green' }}
                  />
                  <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="h6" component="div" color="white">
                      {temperature ? `${temperature.toFixed(1)}°C` : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                {message && (
                  <Box>
                    <Typography variant="h6" sx={{ color: '#ffffff', marginBottom: 1 }}>
                      Experiment Details:
                    </Typography>
                    {renderExperimentDetails(JSON.parse(message))}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} align="center">
                {experimentRunning !== null && (
                  <Button
                    variant="contained"
                    color={experimentRunning ? 'error' : 'primary'}
                    onClick={handleExperimentControl}
                  >
                    {experimentRunning ? 'Stop Experiment' : 'Launch Experiment'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </AccordionDetails>
      </Accordion>

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        aria-labelledby="confirm-dialog-title" 
        aria-describedby="confirm-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#1e1e1e', 
            color: 'white', 
          }
        }}
      >
        <DialogTitle id="confirm-dialog-title" sx={{ color: 'white' }}>
          Stop Experiment
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: 'white' }}>
            Are you sure you want to stop the experiment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary" sx={{ color: 'gray' }}>
            Cancel
          </Button>
          <Button onClick={confirmStopExperiment} color="error" sx={{ color: 'white' }}>
            Stop
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert variant="filled" onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          Device is offline. Please check the connection.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DeviceStatus;
